import Breadcrumb from "components/Common/Breadcrumb"
import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import { FaEye, FaSave, FaSearch } from "react-icons/fa"
import { MdCancel } from "react-icons/md"
import moment from "moment"
import Swal from "sweetalert2"
import Loader from "components/Loader/Loader"
import { fetchData, FetchPutData } from "utils/Common_api_functions"

const allowOnlyNumbers = value => value.replace(/[^0-9]/g, "")

const Expense = () => {
  const [employees, setEmployees] = useState([])
  const [employee, setEmployee] = useState("")
  const [month, setMonth] = useState("")
  const [year, setYear] = useState("")
  const [loading, setLoading] = useState(false)

  const [saving, setSaving] = useState(false)

  const [allExpenses, setAllExpenses] = useState([])
  const [expenseModal, setExpenseModal] = useState(false)

  const ITEMS_PER_PAGE = 50
  const [currentPage, setCurrentPage] = useState(1)

  const [expenseDetails, setExpenseDetails] = useState([])
  const [filteredDetails, setFilteredDetails] = useState([])

  const startYear = 2020
  const currentYear = new Date().getFullYear()

  /* 🔹 Fetch initial data */
  useEffect(() => {
    fetchEmployees()
    fetchExpenses()
  }, [])

  const fetchEmployees = async () => {
    const res = await fetchData(setLoading, "users")
    if (res?.success) setEmployees(res.data)
  }

  const fetchExpenses = async () => {
    const res = await fetchData(setLoading, "expense")
    if (res?.success) {
      setExpenseDetails(res.data)
      setFilteredDetails(res.data)
    } else {
      Swal.fire("Error", "Failed to load expenses", "error")
    }
  }

  /* 🔹 Apply Filters */
  const handleFilter = () => {
    let filtered = [...expenseDetails] // ✅ RAW DATA

    if (employee) {
      filtered = filtered.filter(
        item => String(item.userId) === String(employee)
      )
    }

    if (month) {
      filtered = filtered.filter(
        item => moment(item.createdAt).format("MMMM") === month
      )
    }

    if (year) {
      filtered = filtered.filter(
        item => moment(item.createdAt).format("YYYY") === year
      )
    }

    setFilteredDetails(filtered)
    setCurrentPage(1)
  }

  const groupedExpenses = useMemo(() => {
    const map = {}

    filteredDetails.forEach(item => {
      if (!item.userId || !item.value || !item.type) return

      const date = moment(item.createdAt).format("DD-MM-YYYY")
      const userId = item.userId
      const key = `${date}_${userId}`

      if (!map[key]) {
        map[key] = {
          date,
          userId,
          employee: `${item?.user?.name || ""}${
            item?.user?.employeeId ? ` (${item.user.employeeId})` : ""
          }`,
          Travel: 0,
          Dearness: 0,
          Other: 0,
          OtherReason: "",
          total: 0,
          allexpenses: [], // ✅ initialize array
        }
      }

      const amount = Number(item.value || 0)
      const type = item.type.toLowerCase()

      // ✅ push each expense
      map[key].allexpenses.push({
        id: item.id,
        type: item.type,
        amount,
        description: item.description,
        createdAt: item.createdAt,
      })

      if (type === "travel") {
        map[key].Travel += amount
      } else if (type === "dearness") {
        map[key].Dearness += amount
      } else {
        map[key].Other += amount
        if (item.description) {
          map[key].OtherReason += map[key].OtherReason
            ? `, ${item.description}`
            : item.description
        }
      }

      map[key].total += amount
    })

    return Object.values(map)
  }, [filteredDetails])

  /* 🔹 Pagination */
  const totalPages = Math.ceil(groupedExpenses.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE

  const paginatedData = groupedExpenses.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  )

  /* 🔹 Grand Total */
  const grandTotal = groupedExpenses.reduce((sum, row) => sum + row.total, 0)
  const grandTA = groupedExpenses.reduce((sum, row) => sum + row.Travel, 0)
  const grandDA = groupedExpenses.reduce((sum, row) => sum + row.Dearness, 0)
  const grandOther = groupedExpenses.reduce((sum, row) => sum + row.Other, 0)

  const handleSaveExpenses = async item => {
    const APIdata = await FetchPutData(setLoading, "expense", item?.id, {
      value: item?.amount,
    })
    if (APIdata?.data?.success) {
      Swal.fire({
        icon: "success",
        title: "Expense Updated Successfully.",
        text: APIdata?.data?.message,
      })
      fetchExpenses()
    } else {
      Swal.fire({
        icon: "error",
        title: "Something Went Wrong",
        text: APIdata?.response?.data?.message,
      })
    }
  }

  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Payroll" breadcrumbItem="Employee Expenses" />

      <Card className="mb-3">
        <CardBody>
          {/* 🔹 Filters */}
          <Row className="align-items-center">
            <Col md={2} className="position-relative">
              <Input
                type="select"
                value={employee}
                onChange={e => setEmployee(e.target.value)}
              >
                <option value="">All Employees</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </Input>
              {employee && (
                <MdCancel
                  style={clearIconStyle}
                  onClick={() => setEmployee("")}
                />
              )}
            </Col>

            <Col md={2} className="position-relative">
              <Input
                type="select"
                value={month}
                onChange={e => setMonth(e.target.value)}
              >
                <option value="">All Months</option>
                {moment.months().map(m => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Input>
              {month && (
                <MdCancel style={clearIconStyle} onClick={() => setMonth("")} />
              )}
            </Col>

            <Col md={2} className="position-relative">
              <Input
                type="select"
                value={year}
                onChange={e => setYear(e.target.value)}
              >
                <option value="">All Years</option>
                {Array.from(
                  { length: currentYear - startYear + 1 },
                  (_, i) => startYear + i
                )
                  .reverse()
                  .map(y => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
              </Input>
              {year && (
                <MdCancel style={clearIconStyle} onClick={() => setYear("")} />
              )}
            </Col>

            <Col md={3}>
              <Button
                className="btn-blue rounded-4 px-3"
                onClick={handleFilter}
              >
                <FaSearch /> Search
              </Button>
            </Col>
          </Row>

          {/* 🔹 Table */}
          <Table striped className="mt-4">
            <thead className="table-dark">
              <tr>
                <td>#</td>
                <td>Date</td>
                <td>Employee</td>
                <td>Travel</td>
                <td>Dearness</td>
                <td>Other</td>
                <td>Total</td>
                <td>View</td>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center">
                    No Records Found
                  </td>
                </tr>
              )}

              {paginatedData.map((row, index) => (
                <tr key={index}>
                  <td>{startIndex + index + 1}</td>
                  <td>{row.date}</td>
                  <td>{row.employee}</td>
                  <td>₹ {row.Travel}</td>
                  <td>₹ {row.Dearness}</td>
                  <td>₹ {row.Other}</td>
                  <td className="fw-bold">₹ {row.total}</td>
                  <td>
                    <Button
                      onClick={() => {
                        setAllExpenses(row?.allexpenses), setExpenseModal(true)
                      }}
                    >
                      <FaEye />
                    </Button>
                  </td>
                </tr>
              ))}

              {groupedExpenses.length > 0 && (
                <tr className="table-secondary fw-bold">
                  <td colSpan={3} className="text-end pe-4">
                    Grand Total
                  </td>
                  <td>₹ {grandTA}</td>
                  <td>₹ {grandDA}</td>
                  <td>₹ {grandOther}</td>
                  <td>₹ {grandTotal}</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </Table>
        </CardBody>
      </Card>

      <Modal isOpen={expenseModal} size="lg">
        <ModalHeader toggle={() => setExpenseModal(false)}>
          Expense Details
        </ModalHeader>
        <ModalBody>
          <Table>
            <thead className="table-dark">
              <tr>
                <td>#</td>
                <td>Type</td>
                <td>Description</td>
                <td>Amount</td>
                <td>Action</td>
              </tr>
            </thead>
            <tbody>
              {allExpenses.map((item, index) => (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td className="text-capitalize">{item.type}</td>
                  <td>{item.description || "--"}</td>
                  <td>
                    <Input
                      type="text"
                      className="form-control rounded-4  w-auto"
                      value={item.amount}
                      onChange={e => {
                        const value = allowOnlyNumbers(e.target.value)

                        setAllExpenses(prev =>
                          prev.map(exp =>
                            exp.id === item.id ? { ...exp, amount: value } : exp
                          )
                        )
                      }}
                    />
                  </td>
                  <td>
                    <Button
                      className="btn btn-blue "
                      disabled={saving}
                      onClick={() => handleSaveExpenses(item)}
                    >
                      <FaSave />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ModalBody>
      </Modal>
    </div>
  )
}

/* 🔹 Clear icon style */
const clearIconStyle = {
  position: "absolute",
  right: "20%",
  top: "50%",
  transform: "translateY(-50%)",
  cursor: "pointer",
}

export default Expense
